/**
 * Portal do cliente — cliente de rede da F1 (fundação de acesso).
 *
 * Espelho fino de `apps/api/src/routes/portal/*`, no mesmo molde de
 * `lib/api/me.ts` e `lib/api/dashboard.ts`: cookie de sessão sempre incluído
 * (better-auth), non-2xx a lançar, contrato escrito à mão (o idioma desta base
 * de código é um contrato por cliente, não um DTO partilhado).
 *
 * ⚠️ ESCOPO: identidade (`GET /api/portal/me`, F1), alocados (F2), a vitrine
 * (F3) e o briefing do Início (Story 30.2). Cada bloco entrou quando a rota do
 * outro lado passou a existir — um
 * cliente que fala com uma rota que não existe é um erro que só aparece em
 * produção.
 *
 * PORQUE 401 E 403 LANÇAM (e o `fetchMe` não)
 * -------------------------------------------
 * O `GET /api/me` devolve `{ user: null }` num 401 porque a pergunta dele é
 * "há sessão?" — e "não há" é uma resposta legítima, não uma falha. Aqui a
 * pergunta é outra: "quem é o cliente desta sessão?". Chegar a esta chamada
 * já pressupõe uma sessão de cliente (a guarda de `/portal` exige-a), logo
 * qualquer recusa é um facto que a tela tem de contar a alguém:
 *
 *   • 401 — a sessão morreu entre o `/api/me` e este pedido;
 *   • 403 — a fronteira do §4 da spec: ou o papel não é `customer`, ou é e não
 *     tem `customer_id`. Nenhum dos dois se resolve recarregando.
 *
 * Por isso o erro carrega o STATUS (`ApiError`), como em `lib/api/users.ts`:
 * sem ele a tela não conseguiria oferecer a ação certa — entrar de novo, ou
 * sair.
 */
import type { PortalMode, ProfessionalSeniority } from '@caliber/shared'
import { API_BASE } from '@/lib/api'
import { portalViewHeaders } from '@/lib/portal-view'
import { parseProblemJson, readProblemMessage } from '@/lib/api/problem'
import { publicFeedbackErrorMessage } from '@/lib/api/public-catalog'
import { ApiError } from '@/lib/api/users'
import type { AcaoCitada } from '@/lib/api/professional-notes'
import type { AllocationReviewSummary, CreateAllocationReview } from '@caliber/shared'

export { ApiError }

/** O utilizador da sessão, como o portal o vê. */
export interface PortalUser {
  id: string
  name: string
  email: string
  /**
   * URL da foto, quando existe. É um URL PRONTO A USAR num `src` — o portal
   * não o compõe nem lhe acrescenta cache-buster, ao contrário do
   * `meAvatarUrl()` das telas internas, cujo endereço é fixo e por isso precisa
   * de `?v=`. Ausente/`null` = sem foto, e o avatar cai no gradiente.
   */
  avatarUrl?: string | null
}

/** O cliente (a empresa) a que a sessão pertence. Vem SEMPRE da sessão. */
export interface PortalCustomer {
  id: string
  name: string
  /** Logotipo, quando existe. Mesma leitura do `avatarUrl` acima. */
  logoUrl?: string | null
}

/**
 * As áreas/projetos que esta sessão vê (Story 24.6). Os profissionais que o
 * portal devolve JÁ vêm filtrados por isto no servidor; nunca serve para decidir
 * o que mostrar.
 *
 * `allAreas: true` ⇒ `areas` vem vazio ("todas" não é uma lista: as áreas com
 * gente saem da lista de profissionais). Sem acesso decidido, o servidor manda
 * `{ allAreas: false, areas: [] }` — e a lista de profissionais vem vazia.
 *
 * ⚠️ O FILTRO POR ÁREA DA LISTA (Story 24.7) NÃO LÊ ISTO: tira as áreas da própria
 * lista de profissionais (`area` de cada um). A lista já vem recortada pelo escopo,
 * portanto as áreas dela são exatamente as do escopo que têm gente — e uma área do
 * escopo sem ninguém só daria uma opção que filtra para o vazio.
 */
export interface PortalScope {
  allAreas: boolean
  areas: Array<{ id: string; name: string }>
}

/** Resposta de `GET /api/portal/me`. */
export interface PortalMe {
  /**
   * Como a sessão entrou (issue #584): `customer` (o usuário do cliente), ou, para a
   * Genesis numa vista, `member` (o perfil de cliente) ou `account_manager` (a gestora
   * de conta, Story 35.9).
   */
  mode: PortalMode
  user: PortalUser
  customer: PortalCustomer
  scope: PortalScope
}

export const PORTAL_ME_URL = `${API_BASE}/portal/me`

/** A frase por omissão quando o servidor não manda uma própria. */
function portalMeFallback(status: number): string {
  if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.'
  if (status === 403) return 'Esta área é exclusiva de clientes da Genesis Consulting.'
  return `Não foi possível carregar o portal (${status})`
}

/**
 * `GET /api/portal/me` — quem está autenticado e a que cliente pertence.
 *
 * O `customer_id` NUNCA viaja no pedido: resolve-se da sessão no servidor
 * (regra de ouro do §4 da spec — um tenant no URL seria um IDOR à espera).
 *
 * A exceção é a vista da Genesis (issue #584): o cliente escolhido vai no header
 * (`portalViewHeaders`, em todo pedido deste arquivo), e o servidor confere-o contra
 * a concessão da sessão antes de o usar. O usuário do cliente não tem vista.
 */
export async function fetchPortalMe(params: { signal?: AbortSignal } = {}): Promise<PortalMe> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { Accept: 'application/json', ...portalViewHeaders() },
  }
  if (params.signal) init.signal = params.signal

  const res = await fetch(PORTAL_ME_URL, init)
  if (!res.ok) {
    throw new ApiError(res.status, await readProblemMessage(res, portalMeFallback(res.status)))
  }
  return (await res.json()) as PortalMe
}

/* ═══════════════════════════════════════════ F2 — ALOCADOS ═══
 * `apps/api/src/routes/portal/allocations.ts`. O `customer_id` continua a não
 * viajar em pedido nenhum: todas estas rotas o resolvem da sessão.
 * ============================================================ */

/**
 * Como a pessoa aparece ao cliente. **UNIÃO DISCRIMINADA, e é de propósito.**
 *
 * ⚠️ O `kind` É A FONTE DE VERDADE SOBRE O QUE HÁ PARA DESENHAR — não o
 * `isLive` da alocação. Os dois são quase sempre iguais, e é justamente esse
 * "quase" que faz o defeito: o `displayFor` do servidor cai no codinome sempre
 * que a leitura identificada devolver `null` (a porta de `identity.ts` traz o
 * predicado de alocação viva EMBUTIDO na consulta, e pode responder `null`
 * entre o SELECT da alocação e a leitura da pessoa). Nesse caso `isLive`
 * continua `true` enquanto `kind` já é `'codename'` — e uma tela que ramifica
 * por `isLive` mostraria campos que não vieram.
 *
 * A união é a segunda metade dessa garantia: `display.name` não existe no ramo
 * `codename`, portanto `if (isLive) { display.name }` nem sequer compila. Quem
 * quiser escrever o defeito tem de forçar um cast — e aí é uma decisão, não um
 * descuido.
 */
export type PortalPersonDisplay =
  | {
      kind: 'identified'
      name: string
      /**
       * O e-mail GENESIS da pessoa (Story 24.10, D11) — o único e-mail que o portal
       * recebe. O pessoal saiu do contrato, não só da tela: não há campo onde ele
       * caiba. `null` = a Genesis ainda não o cadastrou, e a tela mostra "—".
       */
      genesisEmail: string | null
      phone: string | null
      linkedinUrl: string | null
      /**
       * A senioridade que a Genesis cadastrou (Story 34.1); `null` = sem cadastro.
       * Só no ramo identificado: é um facto da pessoa, e o codinome não o tem.
       */
      seniority: ProfessionalSeniority | null
      /**
       * URL da foto, PRONTO a usar num `src` (já traz o `?v=`) — a mesma leitura do
       * `avatarUrl` de `PortalUser`. `null` = sem foto, e o avatar cai nas iniciais.
       */
      photoUrl: string | null
    }
  | { kind: 'codename'; codename: string }

/** Uma alocação, como o portal a vê. Sem `professionalId` — o servidor não o envia. */
export interface PortalAllocation {
  id: string
  /** O papel/projeto NO cliente. Texto livre, escrito pela Genesis. */
  roleTitle: string
  /** Data de CONTRATO, `YYYY-MM-DD` — não um instante. Use `formatDay`. */
  startedOn: string
  /**
   * O término da alocação: `null` sem término, ou a data marcada (`YYYY-MM-DD`).
   * Desde a Story 27.1 a alocação encerrada sai do portal (lista e detalhe), e a API
   * só devolve vivas; desde a Story 28.1 uma viva pode ter o término marcado para
   * uma data FUTURA (está viva até à véspera), e é essa data que vem aqui.
   */
  endedOn: string | null
  /**
   * A alocação em vigor em `hoje` (`isInEffectOn`: viva e já começada, Story
   * 29.2), calculado pelo servidor — e por isso SEMPRE `true` no portal desde a
   * Story 27.1. Diz respeito ao ESTADO DA RELAÇÃO,
   * não à pessoa: NUNCA use isto para decidir o que mostrar DA PESSOA — a alocação
   * viva pode vir por codinome (ver o aviso em `PortalPersonDisplay`).
   */
  isLive: boolean
  /**
   * A área/projeto da atribuição ATUAL (Story 24.7); `null` = sem área. O `id` é o
   * valor do filtro da lista. É da alocação, como o papel e as datas.
   */
  area: PortalAllocationArea | null
  /** O gestor da atribuição ATUAL; `null` = sem gestor. Nome, e-mail, cargo e foto. */
  manager: PortalAllocationManager | null
  /**
   * O account manager da Genesis para este cliente (Story 34.2) — o mesmo em todas as
   * alocações; `null` = o cliente não tem um (o bloco some).
   */
  accountManager: PortalAccountManager | null
  person: { display: PortalPersonDisplay }
  /**
   * A nota de entrada (Story 34.5): o `final_score` do perfil público da candidatura
   * que deu origem à alocação, com uma casa. SÓ NO DETALHE: a lista não a traz
   * (`undefined`), e a ficha aberta pela cache da lista mostra-a quando a leitura
   * própria chega. `null` = não há (sem candidatura ligada, perfil em rascunho, ou a
   * pessoa por codinome).
   */
  entryScore?: number | null
}

/** A área/projeto no cliente. */
export interface PortalAllocationArea {
  id: string
  name: string
}

/** O gestor no cliente — um contato da empresa. Sem id, de propósito: o servidor não o envia. */
export interface PortalAllocationManager {
  name: string
  /** `null` = a Genesis não tem o e-mail dele. */
  email: string | null
  /** O cargo no cliente («Head de Produto»), Story 34.2; `null` = sem cargo. */
  title: string | null
  /**
   * O endereço PRONTO da foto da conta do portal do gestor (`…/manager/photo?v=…`);
   * `null` sem conta ou sem foto.
   */
  photoUrl: string | null
}

/** O account manager da Genesis — sem id, de propósito: o servidor não o envia. */
export interface PortalAccountManager {
  name: string
  email: string
  /** O endereço PRONTO da foto (`…/account-manager/photo?v=…`); `null` sem foto. */
  photoUrl: string | null
}

/**
 * O tipo do apontamento. Espelha `AllocationNoteKindSchema` de @caliber/shared/dtos
 * — os CINCO valores (Story 24.3; o 1:1 desde a 31.4).
 *
 * Eram três, e o quarto (`milestone`) chegava à mesma: a Genesis partilha um Marco,
 * o servidor devolve-o, e a tabela de rótulos do portal, indexada por este tipo,
 * não tinha a chave — o selo saía vazio, sem erro nenhum. Os rótulos vêm agora de
 * `vocabulary.ts`, indexado pelo tipo do lado da Genesis, e o compositor do portal
 * envia o tipo daquele lado. Se os dois divergirem, num sentido deixa de compilar
 * o selo; no outro, o envio.
 *
 * O quinto, `one_on_one` ("1:1", Story 31.4), entrou aqui pela mesma razão: o
 * cliente LÊ um 1:1 que a Genesis partilhou. Não o ESCREVE — o compositor do portal
 * só oferece `KIND_DO_CLIENTE`, e o servidor responde 400 a um 1:1 vindo daqui.
 *
 * O sexto, `review` ("Avaliação", Story 34.3), é o apontamento que a avaliação dos 4
 * pilares grava no Journal. Nenhum compositor o oferece: só `createPortalAllocationReview`
 * o produz, e o POST de notas recusa-o com 400.
 */
export type PortalNoteKind = 'praise' | 'concern' | 'note' | 'milestone' | 'one_on_one' | 'review'

/**
 * Um apontamento da linha do tempo PARTILHADA.
 *
 * O cliente nunca recebe as notas internas da Genesis — o `WHERE` de
 * `sharedNotesOfLiveAllocation` não sabe devolvê-las. Mas a lista **não é toda dele**:
 * `authorSide` distingue quem escreveu, e a tela tem de o mostrar.
 */
export interface PortalAllocationNote {
  id: string
  authorSide: 'customer' | 'genesis'
  kind: PortalNoteKind
  body: string
  /** ISO-8601 completo — é um INSTANTE. Use `formatMoment`. */
  occurredAt: string
  /** `null` quando o autor já não existe (o LEFT JOIN não inventa um nome). */
  authorName: string | null
  /** As ações do plano desta passagem que o apontamento cita (Story 37.2). */
  actions: AcaoCitada[]
  /** Se quem lê pode excluir o apontamento: é o autor, e não é uma Avaliação (37.4). */
  deletable: boolean
}

export interface CreatePortalNoteInput {
  kind: PortalNoteKind
  body: string
  /** ISO-8601. Omitido = agora, decidido pelo servidor. */
  occurredAt?: string
  /** As ações do plano desta passagem (Story 37.2). Omitido = nenhuma. */
  actionIds?: string[]
}

const acceptJson = { Accept: 'application/json' }
const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

/**
 * A frase por omissão de qualquer rota de alocados.
 *
 * 401 e 403 dizem o mesmo que na identidade — a sessão morreu, ou o papel não é
 * de cliente. **404 é a tenancy**: o servidor devolve o MESMO 404 para "não
 * existe" e para "é de outro cliente", de propósito (um 403 confirmaria que a
 * alocação existe, e a existência já é informação sobre um concorrente). A
 * frase daqui tem de ser igualmente indistinta.
 */
function allocationsFallback(status: number, plural: boolean): string {
  if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.'
  if (status === 403) return 'Esta área é exclusiva de clientes da Genesis Consulting.'
  if (status === 404) return 'Esta alocação não existe, ou não pertence à sua empresa.'
  return plural
    ? `Não foi possível carregar os profissionais (${status})`
    : `Não foi possível carregar a alocação (${status})`
}

async function getJson<T>(
  url: string,
  fallback: (status: number) => string,
  signal?: AbortSignal,
): Promise<T> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { ...acceptJson, ...portalViewHeaders() },
  }
  if (signal) init.signal = signal

  const res = await fetch(url, init)
  if (!res.ok) {
    throw new ApiError(res.status, await readProblemMessage(res, fallback(res.status)))
  }
  return (await res.json()) as T
}

/**
 * `GET /api/portal/allocations` — quem este cliente tem alocado HOJE. As
 * encerradas não vêm (Story 27.1): saem do portal.
 *
 * SEM paginação, porque o contrato não a tem: a lista de alocados de um cliente
 * é gente a trabalhar, não registos de log. A ordem vem do servidor (por início
 * mais recente) e a tela NÃO a reordena — reordenar aqui seria ter duas opiniões
 * sobre a mesma pergunta.
 */
export async function listPortalAllocations(
  params: { signal?: AbortSignal } = {},
): Promise<PortalAllocation[]> {
  const body = await getJson<{ allocations: PortalAllocation[] }>(
    `${API_BASE}/portal/allocations`,
    (s) => allocationsFallback(s, true),
    params.signal,
  )
  return body.allocations
}

/**
 * `GET /api/portal/allocations/:id` — a mesma vista, uma alocação. 404 se não for
 * deste cliente, do escopo do usuário, ou se tiver sido encerrada (Story 27.1).
 */
export async function getPortalAllocation(
  id: string,
  params: { signal?: AbortSignal } = {},
): Promise<PortalAllocation> {
  return getJson<PortalAllocation>(
    `${API_BASE}/portal/allocations/${encodeURIComponent(id)}`,
    (s) => allocationsFallback(s, false),
    params.signal,
  )
}

/** `GET /api/portal/allocations/:id/notes` — a linha do tempo partilhada. */
export async function listPortalAllocationNotes(
  id: string,
  params: { signal?: AbortSignal } = {},
): Promise<PortalAllocationNote[]> {
  const body = await getJson<{ notes: PortalAllocationNote[] }>(
    `${API_BASE}/portal/allocations/${encodeURIComponent(id)}/notes`,
    (s) =>
      s === 404
        ? 'Esta alocação não existe, ou não pertence à sua empresa.'
        : `Não foi possível carregar os apontamentos (${s})`,
    params.signal,
  )
  return body.notes
}

/**
 * `POST /api/portal/allocations/:id/notes` — 201 + o apontamento criado.
 *
 * ⚠️ O corpo NÃO leva `authorSide` nem `visibility`, e não é esquecimento: os
 * dois são literais no servidor e nem existem no schema de entrada. Enviá-los
 * daqui seria escrever código que o servidor descarta em silêncio — e deixaria
 * a impressão de que a tela os controla.
 *
 * ⚠️ O 404 É O CASO REAL DE NEGÓCIO (Story 27.1): a alocação foi encerrada entre
 * o carregamento da tela e o envio, e saiu do portal. O servidor responde o 404 de
 * "outro cliente", com o `detail` que leva o id da alocação — que, no compositor,
 * se lê como erro de sistema. Por isso o 404 tem uma frase PRÓPRIA, neutra (não diz
 * "encerrada", que o servidor também não distingue). As outras recusas trazem a
 * frase do servidor tal como veio.
 */
export async function createPortalAllocationNote(
  id: string,
  input: CreatePortalNoteInput,
): Promise<PortalAllocationNote> {
  const res = await fetch(`${API_BASE}/portal/allocations/${encodeURIComponent(id)}/notes`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...jsonHeaders, ...portalViewHeaders() },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      res.status === 404
        ? 'Esta alocação não está mais disponível no portal.'
        : await readProblemMessage(res, `Não foi possível registrar o apontamento (${res.status})`),
    )
  }
  return (await res.json()) as PortalAllocationNote
}

/**
 * `POST /api/portal/allocations/:id/notes/:noteId/deletion` — quem escreveu exclui o
 * seu apontamento pelo portal, com o motivo (Story 37.4).
 *
 * ⚠️ COM `portalViewHeaders()`, como o `createPortalAllocationNote`: sem o header, uma
 * escrita da Genesis no portal (perfil de cliente ou gestora de conta) não entra.
 */
export async function deletePortalAllocationNote(
  allocationId: string,
  noteId: string,
  reason: string,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/portal/allocations/${encodeURIComponent(allocationId)}/notes/${encodeURIComponent(noteId)}/deletion`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { ...jsonHeaders, ...portalViewHeaders() },
      body: JSON.stringify({ reason }),
    },
  )
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readProblemMessage(res, `Não foi possível excluir o apontamento (${res.status})`),
    )
  }
}

/* ═══════════════════════════════════ A AVALIAÇÃO DOS 4 PILARES ═══
 * Story 34.3 (Epic 34). `apps/api/src/routes/portal/allocations.ts`, a mesma porta
 * das notas: o cliente da sessão, as áreas do usuário e a alocação em vigor.
 *
 * O contrato vive em `@caliber/shared` (`allocation-reviews.ts`) e é reexportado
 * daqui: as stories 34.5 (score e cartões) e 34.6 (evolução) leem o resumo por este
 * ficheiro, como o resto do portal.
 * ============================================================ */

export type {
  AllocationReviewSummary,
  CreateAllocationReview,
  ReviewPillar,
  ReviewQuarterSummary,
  ReviewableQuarter,
} from '@caliber/shared'

/** O que o POST devolve: a avaliação (sem a pessoa e sem o autor) e o apontamento do Journal. */
export interface CreatedPortalReview {
  review: {
    id: string
    quarter: string
    scores: CreateAllocationReview['scores']
    comments: Record<keyof CreateAllocationReview['scores'], string | null>
    comment: string | null
    /** ISO-8601 — um INSTANTE. */
    createdAt: string
  }
  note: PortalAllocationNote
}

/**
 * `GET /api/portal/allocations/:id/reviews/summary` — por trimestre, a média de cada
 * pilar e o score (a mais recente de cada autor), e os trimestres que se podem
 * avaliar. Sem avaliação, `quarters` vem vazio: a tela diz que ainda não há, e não
 * mostra zero.
 */
export async function getPortalAllocationReviewSummary(
  id: string,
  params: { signal?: AbortSignal } = {},
): Promise<AllocationReviewSummary> {
  return getJson<AllocationReviewSummary>(
    `${API_BASE}/portal/allocations/${encodeURIComponent(id)}/reviews/summary`,
    (s) =>
      s === 404
        ? 'Esta alocação não existe, ou não pertence à sua empresa.'
        : `Não foi possível carregar as avaliações (${s})`,
    params.signal,
  )
}

/**
 * `POST /api/portal/allocations/:id/reviews` — 201 + a avaliação e o apontamento.
 *
 * O 404 tem a frase do `createPortalAllocationNote`, pela mesma razão: a alocação
 * saiu do portal entre o carregamento da tela e o envio. As outras recusas (o
 * trimestre que não se pode avaliar, uma nota fora de 1 a 10) trazem a frase do
 * servidor.
 */
export async function createPortalAllocationReview(
  id: string,
  input: CreateAllocationReview,
): Promise<CreatedPortalReview> {
  const res = await fetch(`${API_BASE}/portal/allocations/${encodeURIComponent(id)}/reviews`, {
    method: 'POST',
    credentials: 'include',
    headers: { ...jsonHeaders, ...portalViewHeaders() },
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    throw new ApiError(
      res.status,
      res.status === 404
        ? 'Esta alocação não está mais disponível no portal.'
        : await readProblemMessage(res, `Não foi possível enviar a avaliação (${res.status})`),
    )
  }
  return (await res.json()) as CreatedPortalReview
}

/* ═══════════════════════════════════════════ F3 — A VITRINE ═══
 * `apps/api/src/routes/portal/showcase.ts`. O `customer_id` continua a não
 * viajar em pedido nenhum.
 *
 * ------------------------------------------------------------------
 * ⚠️ O QUE ESTE CONTRATO NÃO TEM, E NÃO VAI TER
 * ------------------------------------------------------------------
 * Não há `name`, `email`, `phone`, `linkedinUrl`, `citation`, `evidence`,
 * `analysis` nem o texto do relatório. Não é omissão a preencher depois: é a
 * decisão central da spec (§2.3 e D5). A prosa que existe aqui — `summary`,
 * `strengths`, `development` — é GERADA de novo sob instrução de não identificar
 * ninguém (D13), e passa por uma trava determinística antes de o perfil ficar
 * visível (§3.4). A do relatório interno entrega a identidade na primeira frase,
 * e por isso nunca é reaproveitada.
 *
 * A garantia real está na FORMA DOS DADOS (D8): tudo isto vem de
 * `public_profiles`, uma tabela sem coluna nenhuma onde um nome caiba. Este
 * ficheiro é o espelho dessa forma, não um filtro sobre ela — se um dia um campo
 * de PII aparecer aqui, o problema está três camadas abaixo.
 *
 * A fronteira do anonimato é o ESTADO DA RELAÇÃO (D2): ver o nome exige uma
 * alocação VIVA, e alguém na vitrine é, por definição, quem o cliente ainda não
 * alocou. Por isso a vitrine — ao contrário dos alocados — não tem sequer a
 * união discriminada de `PortalPersonDisplay`: não há ramo identificado.
 * ============================================================ */

/**
 * Uma demanda com perfis apresentados.
 *
 * ⚠️ Uma vaga SEM ninguém apresentado não chega aqui — o `INNER JOIN` do
 * servidor exclui-a. A lista responde a "onde é que a Genesis já me mostrou
 * gente", e uma demanda vazia só produziria um clique para um ecrã em branco.
 * A ARQUIVADA também não chega (#434): é o soft-delete da vaga, e sai do portal
 * inteiro.
 */
export interface PortalDemand {
  id: string
  title: string
  /**
   * `closed` é a vaga encerrada (#434): continua na lista, com o selo, e a
   * vitrine dela abre como a de uma aberta. Só sai do Início.
   */
  status: 'open' | 'closed'
  /** Quantos perfis a Genesis apresentou nesta demanda. Nunca zero (ver acima). */
  presentedCount: number
}

/**
 * Um eixo da avaliação, como a vitrine o mostra.
 *
 * `weight` é a SOMA DOS PESOS DAS PERGUNTAS daquele eixo (ver
 * `lib/public-profile-projection.ts` do lado do servidor) — responde a "quanto
 * desta avaliação é este eixo". Sozinho não diz nada a quem lê: só ganha
 * sentido comparado com a soma dos outros, e é assim que a tela o apresenta.
 */
export interface PortalSkillScore {
  label: string
  /** 0–10. */
  score: number
  weight: number
}

/**
 * Uma competência comportamental, como a vitrine a mostra (Story 17.5).
 *
 * ⚠️ SÓ TRÊS CAMPOS, E A LISTA É FECHADA DO LADO DO SERVIDOR
 * (`lib/showcase-profile.ts`). A justificativa escrita sobre a pessoa
 * (`rationale`) e as citações da transcrição NÃO viajam: a vitrine é anónima, e
 * quem está identificado no portal é o CLIENTE, não a pessoa avaliada.
 *
 * `anchor` é texto de RUBRICA — a descrição do nível atribuído, igual para toda
 * a gente nesse nível; com `level` a `null`, o motivo de não se ter observado.
 */
export interface PortalBehavioralCompetency {
  label: string
  /** 1 a 4; `null` = não observada (nunca nível 1). */
  level: 1 | 2 | 3 | 4 | null
  anchor: string
}

/**
 * O perfil apresentado — o que o cliente vê de alguém que ainda não alocou.
 *
 * ⚠️ `codename` É A ÚNICA FORMA DE NOMEAR ESTA PESSOA. É estável por pessoa
 * (deriva do `id` dela, D3), portanto o cliente que a viu noutra demanda
 * reconhece que é a mesma — e é exactamente por isso que ele NÃO é único por
 * linha (D15).
 */
export interface PortalShowcaseProfile {
  /** A candidatura. É sobre ela que o sinal do cliente age — não sobre a pessoa. */
  applicationId: string
  codename: string
  /** O nome da RUBRICA ("Desenvolvedor .NET"), não um facto sobre a pessoa. */
  headline: string | null
  /** Média das avaliações vigentes. `null` = ainda não há nota (≠ nota zero). */
  finalScore: number | null
  /**
   * As competências comportamentais. Vazio = sem leitura comportamental.
   * NÃO há nota comportamental única: os níveis não se somam (rubrica §4.6).
   */
  behavioralCompetencies: PortalBehavioralCompetency[]
  skillScores: PortalSkillScore[]
  /**
   * O que NÃO foi avaliado. Não é defeito a esconder: sem isto o cliente não
   * distingue "foi mal" de "o assunto nem foi abordado".
   */
  coverageGaps: string[]
  summary: string | null
  strengths: string[]
  development: string[]
  /** ISO-8601 — o instante da curadoria. É por ele que o servidor ordena. */
  presentedAt: string | null
  /**
   * O ÚLTIMO sinal do cliente sobre este perfil (Story 23.29); `null` = ninguém
   * sinalizou ainda. É o estado da empresa, e não de quem está com a sessão: um
   * colega pelo portal, ou alguém com o link do catálogo da mesma vaga, muda-o.
   * Só o enum — o comentário que o sinal pode levar não volta na leitura.
   */
  clientSignal: PortalClientSignal | null
}

/** Resposta de `GET /api/portal/demands/:id/showcase`. */
export interface PortalShowcase {
  demand: { id: string; title: string | null }
  profiles: PortalShowcaseProfile[]
  /** ISO-8601 do último retorno da seleção desta vaga (Story 23.29); `null` = nenhum. */
  feedbackSentAt: string | null
}

/** A reação do cliente a um perfil. Espelha `ClientSignalSchema` de @caliber/shared/dtos. */
export type PortalClientSignal = 'interested' | 'declined'

export interface CreatePortalSignalInput {
  signal: PortalClientSignal
  /** Opcional. Máximo 2000 caracteres — o limite de `CreateClientSignalSchema`. */
  note?: string
}

/** O 201 de `POST /api/portal/showcase/:applicationId/signal`. */
export interface PortalSignalReceipt {
  id: string
  applicationId: string
  signal: PortalClientSignal
  note: string | null
  occurredAt: string
}

/** O limite do comentário, igual ao do servidor. */
export const PORTAL_SIGNAL_NOTE_MAX = 2000

/**
 * A frase por omissão da vitrine.
 *
 * ⚠️ O 404 É INDISTINTO DE PROPÓSITO, e a frase daqui tem de o ser também. O
 * servidor devolve o MESMO 404 para "esta demanda não existe", "é de outro
 * cliente" e "não tem ninguém apresentado": distinguir os três revelaria a
 * existência de uma vaga da Genesis a quem não é dono dela — informação sobre um
 * concorrente. Uma frase mais precisa aqui desfaria a indistinção do servidor.
 */
function showcaseFallback(status: number): string {
  if (status === 401) return 'Sua sessão expirou. Entre novamente para continuar.'
  if (status === 403) return 'Esta área é exclusiva de clientes da Genesis Consulting.'
  if (status === 404) return 'Esta vaga não existe, ou não tem perfis apresentados à sua empresa.'
  return `Não foi possível carregar a vitrine (${status})`
}

/**
 * `GET /api/portal/demands` — as demandas com perfis apresentados.
 *
 * SEM paginação, como os alocados: as demandas de um cliente são poucas por
 * natureza. A ordem vem do servidor (as abertas antes das encerradas; em cada
 * grupo, mais perfis primeiro, depois por título) e a tela NÃO a reordena.
 */
export async function listPortalDemands(
  params: { signal?: AbortSignal } = {},
): Promise<PortalDemand[]> {
  const body = await getJson<{ demands: PortalDemand[] }>(
    `${API_BASE}/portal/demands`,
    (s) =>
      s === 401 || s === 403 ? showcaseFallback(s) : `Não foi possível carregar as vagas (${s})`,
    params.signal,
  )
  return body.demands
}

/**
 * `GET /api/portal/demands/:id/showcase` — os perfis apresentados numa demanda.
 *
 * A ordem é a do servidor: mais recentemente apresentado primeiro, que é a
 * ordem da pergunta que a tela faz ("o que há de novo para eu ver?").
 */
export async function getPortalShowcase(
  demandId: string,
  params: { signal?: AbortSignal } = {},
): Promise<PortalShowcase> {
  return getJson<PortalShowcase>(
    `${API_BASE}/portal/demands/${encodeURIComponent(demandId)}/showcase`,
    showcaseFallback,
    params.signal,
  )
}

/**
 * `POST /api/portal/showcase/:applicationId/signal` — 201 + o sinal gravado.
 *
 * ⚠️ APPEND-ONLY: sinalizar duas vezes grava dois eventos. Mudar de ideias é um
 * facto novo, não uma correção do anterior — a mesma doutrina de
 * `candidate_notes`. Não existe (nem vai existir aqui) um DELETE ou um PUT.
 *
 * ⚠️ O `occurredAt` é do SERVIDOR e não vai no corpo: um clique acontece agora, e
 * aceitar a data de quem clica abriria a porta a um sinal datado no futuro.
 */
export async function sendPortalSignal(
  applicationId: string,
  input: CreatePortalSignalInput,
): Promise<PortalSignalReceipt> {
  const res = await fetch(
    `${API_BASE}/portal/showcase/${encodeURIComponent(applicationId)}/signal`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { ...jsonHeaders, ...portalViewHeaders() },
      body: JSON.stringify(input),
    },
  )
  if (!res.ok) {
    throw new ApiError(
      res.status,
      await readProblemMessage(
        res,
        res.status === 404
          ? 'Este perfil não está mais na sua vitrine.'
          : `Não foi possível enviar o sinal (${res.status})`,
      ),
    )
  }
  return (await res.json()) as PortalSignalReceipt
}

/** O 200 de `POST /api/portal/demands/:id/feedback`: o instante em que o e-mail saiu. */
export interface PortalFeedbackReceipt {
  feedbackSentAt: string
}

/**
 * A frase do erro do retorno da seleção pelo portal — pt-BR, escrita aqui, e
 * nunca o `detail` do servidor.
 *
 * O envio do servidor é o MESMO do catálogo público (`lib/selection-feedback.ts`
 * na API), com os mesmos códigos: `no_signals`, `no_recipients`, `cooldown`,
 * `daily_limit` e `too_many_recipients`, e o 502 do Resend. Essas frases são as
 * do catálogo (`publicFeedbackErrorMessage`) — a mesma vaga, o mesmo cliente. O
 * que é só do portal é a porta: 401 e 403 falam da sessão, e o 404 é o da
 * demanda, tão indistinto como o do `GET` ("não existe", "é de outro cliente" e
 * "não tem ninguém apresentado" são a mesma resposta).
 */
export function portalFeedbackErrorMessage(status: number | null, code: string | null): string {
  if (status === 401 || status === 403) return showcaseFallback(status)
  if (status === 404) {
    return 'Não foi possível enviar: esta vaga não tem mais perfis apresentados à sua empresa.'
  }
  return publicFeedbackErrorMessage(status, code)
}

/**
 * `POST /api/portal/demands/:id/feedback` — envia o retorno da seleção (Story
 * 23.29). SEM corpo: o servidor lê o último sinal de cada perfil no momento do
 * envio. O cliente vem da sessão, como em todo o portal.
 *
 * Um erro de rede (o `fetch` rejeita, sem status) também sai como `ApiError`,
 * para o painel ter UMA forma de ler a frase.
 */
export async function sendPortalFeedback(demandId: string): Promise<PortalFeedbackReceipt> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}/portal/demands/${encodeURIComponent(demandId)}/feedback`, {
      method: 'POST',
      credentials: 'include',
      headers: { ...acceptJson, ...portalViewHeaders() },
    })
  } catch {
    throw new ApiError(0, portalFeedbackErrorMessage(null, null))
  }

  if (!res.ok) {
    const corpo = await parseProblemJson(res)
    const code = typeof corpo?.code === 'string' ? corpo.code : null
    throw new ApiError(res.status, portalFeedbackErrorMessage(res.status, code))
  }

  return (await res.json()) as PortalFeedbackReceipt
}

/* ═══════════════════════════════════════ INÍCIO — O BRIEFING ═══
 * `apps/api/src/routes/portal/briefing.ts` (Story 30.2): o que espera por uma ação
 * do cliente, numa ida só. A equipe e os apontamentos são os dos alocados acima — o
 * servidor monta-os pelas mesmas funções —, e as vagas são as ABERTAS da vitrine
 * (#434: a encerrada fica só na lista de Vagas, e a arquivada fora do portal).
 * ============================================================ */

/**
 * Uma vaga ABERTA com perfis na vitrine, e o que ela pede.
 *
 * ⚠️ VALE PARA A EMPRESA INTEIRA, e não para as áreas da sessão: a vitrine não tem
 * área. A equipe e os apontamentos, ao lado, é que vêm recortados pelo escopo.
 */
export interface PortalBriefingVaga {
  id: string
  title: string
  presentedCount: number
  selectedCount: number
  declinedCount: number
  pendingCount: number
  /** Apresentados há 7 dias ou menos. */
  newCount: number
  /**
   * Só os perfis SEM sinal, mais recentemente apresentado primeiro — e só o que o
   * Início mostra. O perfil inteiro lê-se na vitrine (`getPortalShowcase`).
   */
  pending: Array<{
    applicationId: string
    codename: string
    /** `null` = ainda não há nota (≠ nota zero). */
    finalScore: number | null
    isNew: boolean
  }>
  /** Tudo decidido, e nenhum retorno enviado depois do último sinal. */
  feedbackReady: boolean
  /** ISO-8601 do último retorno desta vaga; `null` = nenhum. */
  feedbackSentAt: string | null
}

/**
 * Um apontamento recente: o da linha do tempo, mais a alocação de que é — a lista
 * mistura a equipe inteira. Sem Alertas (`concern`): esses leem-se no detalhe.
 */
export interface PortalBriefingNote extends Omit<PortalAllocationNote, 'kind'> {
  /**
   * A alocação do apontamento — uma das de `team` na mesma resposta. Só numa corrida
   * (uma alocação que entrou no escopo entre as duas leituras do servidor) pode não
   * estar lá, e a tela tem de tolerar isso.
   */
  allocationId: string
  kind: Exclude<PortalNoteKind, 'concern'>
}

/** Resposta de `GET /api/portal/briefing`. */
export interface PortalBriefing {
  /** Pendentes primeiro, depois pela apresentação mais recente. A tela não reordena. */
  vagas: PortalBriefingVaga[]
  /** A mesma lista de `listPortalAllocations`. */
  team: PortalAllocation[]
  /**
   * No máximo 6, os últimos REGISTADOS primeiro — pela data em que entraram no
   * sistema, e não pelo `occurredAt` (que quem escreve escolhe).
   */
  recentNotes: PortalBriefingNote[]
}

/** `GET /api/portal/briefing` — as vagas, a equipe e os últimos apontamentos. */
export async function getPortalBriefing(
  params: { signal?: AbortSignal } = {},
): Promise<PortalBriefing> {
  return getJson<PortalBriefing>(
    `${API_BASE}/portal/briefing`,
    (s) =>
      s === 401 || s === 403 ? showcaseFallback(s) : `Não foi possível carregar o início (${s})`,
    params.signal,
  )
}
